import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserPlus,
  KeyRound,
  Power,
  PowerOff,
  Loader2,
  Copy as CopyIcon,
  Search,
} from "lucide-react";

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ROLES = ["admin", "staff", "coach"];

export default function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  // Newly-created user temp password (shown once).
  const [tempCred, setTempCred] = useState(null);
  // "add user" form state
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    tenant_id: "",
    email: "",
    name: "",
    role: "admin",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tenantFilter) params.set("tenant_id", tenantFilter);
      if (roleFilter) params.set("role", roleFilter);
      const [u, t] = await Promise.all([
        axios.get(`${API}/super-admin/users?${params.toString()}`, {
          headers: authHeader(),
        }),
        axios.get(`${API}/super-admin/tenants`, { headers: authHeader() }),
      ]);
      setUsers(u.data.users || []);
      setTenants(t.data.tenants || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tenantFilter, roleFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = users.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      u.email?.toLowerCase().includes(s) ||
      u.name?.toLowerCase().includes(s) ||
      u.tenant_name?.toLowerCase().includes(s)
    );
  });

  const resetPassword = async (u) => {
    if (!window.confirm(`Generate a new temp password for ${u.email}?`)) return;
    try {
      const { data } = await axios.post(
        `${API}/super-admin/users/${u.id}/reset-password`,
        {},
        { headers: authHeader() }
      );
      setTempCred(data);
      const es = data.email_sent;
      if (es && es.ok) {
        toast.success(`Password reset — new temp password emailed to ${data.email}`);
      } else if (es && es.reason === "resend_not_configured") {
        toast.warning("Password reset — email NOT sent (platform Resend key not configured). Copy the temp password below.", { duration: 10000 });
      } else {
        toast.success("Password reset — copy the temp password now");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.patch(
        `${API}/super-admin/users/${u.id}`,
        { is_active: !u.is_active },
        { headers: authHeader() }
      );
      toast.success(u.is_active ? "Deactivated" : "Reactivated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.tenant_id || !form.email) {
      toast.error("Tenant and email are required");
      return;
    }
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/super-admin/users`, form, {
        headers: authHeader(),
      });
      setTempCred({
        email: data.user.email,
        tenant_id: data.user.tenant_id,
        temp_password: data.temp_password,
      });
      setForm({ tenant_id: "", email: "", name: "", role: "admin" });
      setShowAdd(false);
      toast.success("User created — copy the temp password now");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setAdding(false);
    }
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-6" data-testid="super-admin-users-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Tenant Users</h1>
          <p className="text-sm text-slate-500">
            Manage admin, staff and coach accounts across every tenant.
          </p>
        </div>
        <button
          onClick={() => setShowAdd((s) => !s)}
          data-testid="show-add-user-btn"
          className="bg-slate-900 text-white rounded px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-slate-800"
        >
          <UserPlus className="w-4 h-4" />
          Add user
        </button>
      </header>

      {/* Temp credential card (shown once after create / reset) */}
      {tempCred && (
        <section
          className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 space-y-2"
          data-testid="temp-cred-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-sm">
                Temp credentials for{" "}
                <span className="font-mono">{tempCred.email}</span>
              </h3>
              <p className="text-xs text-slate-600">
                Copy now — the password is only shown once.
              </p>
            </div>
            <button
              onClick={() => setTempCred(null)}
              className="text-xs text-slate-500"
              data-testid="dismiss-temp-cred"
            >
              Dismiss
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded px-3 py-2 font-mono text-sm">
            <span className="text-slate-500">password:</span>
            <span className="flex-1">{tempCred.temp_password}</span>
            <button
              onClick={() => copy(tempCred.temp_password, "Password")}
              data-testid="copy-temp-cred"
              className="text-emerald-700 hover:text-emerald-900"
            >
              <CopyIcon className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {/* Add user form */}
      {showAdd && (
        <section
          className="bg-white border border-slate-200 rounded-lg p-5"
          data-testid="add-user-form"
        >
          <form className="grid grid-cols-1 md:grid-cols-4 gap-3" onSubmit={submitCreate}>
            <select
              data-testid="add-user-tenant"
              value={form.tenant_id}
              onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Choose tenant…</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <input
              data-testid="add-user-email"
              type="email"
              placeholder="user@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
            />
            <input
              data-testid="add-user-name"
              placeholder="Display name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
            />
            <select
              data-testid="add-user-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
            >
              {ROLES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
            <button
              data-testid="submit-add-user"
              type="submit"
              disabled={adding}
              className="md:col-span-4 bg-slate-900 text-white rounded px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                "Create + generate temp password"
              )}
            </button>
          </form>
        </section>
      )}

      {/* Filters */}
      <section className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="users-search"
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-slate-300 rounded text-sm w-72"
          />
        </div>
        <select
          data-testid="users-tenant-filter"
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm"
        >
          <option value="">All tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          data-testid="users-role-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm"
        >
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} users</span>
      </section>

      {/* Table */}
      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3 text-slate-700">{u.name || "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        u.role === "admin"
                          ? "bg-rose-100 text-rose-800"
                          : u.role === "staff"
                          ? "bg-sky-100 text-sky-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="text-xs">
                      {u.tenant_name || "—"}
                      <div className="font-mono text-slate-400">{u.tenant_id}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.is_active === false ? (
                      <span className="text-xs text-rose-700 inline-flex items-center gap-1">
                        <PowerOff className="w-3 h-3" /> inactive
                      </span>
                    ) : (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                        <Power className="w-3 h-3" /> active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => resetPassword(u)}
                      data-testid={`reset-pwd-${u.id}`}
                      className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 hover:bg-slate-100 px-2 py-1 rounded"
                    >
                      <KeyRound className="w-3 h-3" />
                      Reset password
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      data-testid={`toggle-active-${u.id}`}
                      className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded ${
                        u.is_active === false
                          ? "text-emerald-700 hover:bg-emerald-50"
                          : "text-rose-700 hover:bg-rose-50"
                      }`}
                    >
                      {u.is_active === false ? (
                        <>
                          <Power className="w-3 h-3" /> Reactivate
                        </>
                      ) : (
                        <>
                          <PowerOff className="w-3 h-3" /> Deactivate
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
